import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import type { UpdateCompanySettingsInput } from '@erp/data-access/interfaces';

export const SETTINGS_KEY = ['company-settings'] as const;
export const SETTINGS_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: 24 * 60 * 60 * 1000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: false,
  // Critical: prevents a feedback loop during onboarding. A user with no
  // tenant yet causes getCompanySettings() to throw `not_in_tenant`, which
  // lands the query in `error` state with data=undefined. By default,
  // TanStack treats a NEW observer attaching to such a query as a reason
  // to refetch (since data is missing). BrandThemeProvider toggles
  // `brandReady` based on this query's state, which unmounts/remounts the
  // route subtree. Every remount adds a new observer (via the children's
  // useCompanySettings/useTenantWindowIcon path), which triggers another
  // refetch, which puts the query back in `pending` → `brandReady` flips
  // off → children unmount → … infinite loop. Setting retryOnMount=false
  // tells TanStack to leave the errored query alone on subsequent mounts;
  // the existing `not_in_tenant` error is kept and BrandThemeProvider's
  // settled `brandReady=true` doesn't flip back.
  retryOnMount: false,
} as const;

export function useCompanySettings(enabled = true) {
  const ds = useDataService();
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => ds.settings.getCompanySettings(),
    ...SETTINGS_QUERY_OPTIONS,
    enabled,
  });
}

export function useUpdateCompanySettings() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCompanySettingsInput) => ds.settings.updateCompanySettings(input),
    onSuccess: (settings) => {
      qc.setQueryData(SETTINGS_KEY, settings);
    },
  });
}
